import type { EntityState } from '@reduxjs/toolkit';
import {
	createAsyncThunk,
	createEntityAdapter,
	createSlice,
} from '@reduxjs/toolkit';
import lodash from 'lodash';
import { toastr } from '~/helpers/Toastr';
import * as User from '~/helpers/User';
import type { fetchDssiCodesForCustomerAndSupplierArgs } from '~/Slices/DistributionCentersSlice';
import {
	fetchDistributionCentersForDssiCodes,
	fetchDssiCodesForCustomerAndSupplier,
} from '~/Slices/DistributionCentersSlice';
import type {
	IDistributionCenter,
	IProduct,
	IProductHeader,
} from '~/Slices/schema';
import { LoadingStatus } from '~/Slices/schema';
import type { IAppState } from '~/appstate';
import { ApiRequestError, ApiServerError, unwrapFetch2 } from '../fetch';
import { LocalStorage } from '../helpers';
import * as Hub from '../ogm-hub';
import { search } from './MetaProductPage/MetaProductPageSlice';
import { fetchHeaderLevelPrices } from './ProductHeaderPricesSlice';
import type {
	FetchSearchResults,
	FetchSearchResultsParams,
} from './ProductHeadersSlice';
import { fetchSearchResults, sortPackSizeFacet } from './ProductHeadersSlice';
import type { ProductsGroupedBySupplier } from './ProductUsageHistorySlice';
import { getProductUsageHistory } from './ProductUsageHistorySlice';
import { fetchCurrentCustomerSuppliers } from './SuppliersSlice';

export interface SupplierInfo extends Hub.Supplier {
	distributionCenterCodes: string[];
}

export type IBlueprintCountsState = EntityState<Hub.BlueprintCounts>;
export function blueprintCountId(count: Hub.BlueprintCounts) {
	return `${count.corpSupplierCode}|${count.productNumber}`;
}
export const blueprintCountsAdapter = createEntityAdapter<Hub.BlueprintCounts>({
	selectId: blueprintCountId,
});
export const fetchBlueprintCounts = createAsyncThunk(
	'productSearchPage/fetchBlueprintCounts',
	async (params: Hub.SupplierGroupArgs[], { rejectWithValue }) => {
		try {
			const result = await unwrapFetch2(
				Hub.BlueprintControllerV3.GetNumberOfBlueprintsContainingProducts(
					params
				)
			);
			return result.results;
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export type IRestrictionCountsState =
	EntityState<Hub.ProductWithRestrictionsListCount>;
export function restrictionCountId(
	count: Hub.ProductWithRestrictionsListCount
) {
	return `${count.corpSupplierCode}|${count.productNumber}`;
}
export const restrictionCountsAdapter =
	createEntityAdapter<Hub.ProductWithRestrictionsListCount>({
		selectId: restrictionCountId,
	});
export const fetchRestrictionCounts = createAsyncThunk(
	'productSearchPage/fetchRestrictionCounts',
	async (
		params: Hub.GetNumberOfRestrictionListsContainingProductsArgs[],
		{ rejectWithValue }
	) => {
		try {
			const result = await unwrapFetch2(
				Hub.RestrictionsController.GetNumberOfRestrictionListsContainingProducts(
					params
				)
			);
			return result.results;
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export interface ISearchFiltersAvailable {
	supplierFacet: Hub.SearchFacet[];
	categoryLevel1Facet: Hub.SearchFacet[];
	categoryLevel2Facet: Hub.SearchFacet[];
	categoryLevel3Facet: Hub.SearchFacet[];
	categoryLevel4Facet: Hub.SearchFacet[];
	brandFacet: Hub.SearchFacet[];
	packSizeFacet: Hub.SearchFacet[];
}

export interface IProductSearchPageState {
	selectedProductKey: IProduct;
	searchStatus: LoadingStatus;
	pricingStatus: LoadingStatus;
	usageStatus: LoadingStatus;
	currentPage: IProduct[];
	totalPages: number;
	totalResults: number;
	searchParams: FetchSearchResultsParams;
	filters: ISearchFiltersAvailable;
	blueprintCounts: IBlueprintCountsState;
	restrictionCounts: IRestrictionCountsState;
	suppliers: SupplierInfo[];
	distributionCenterOptions: IDistributionCenter[];
	selectedDistributionCenters: IDistributionCenter[];
}

const initialState: IProductSearchPageState = {
	selectedProductKey: {
		corpSupplierCode: '',
		supplierProductNumber: '',
		unitMeasureCode: null,
	},
	searchStatus: LoadingStatus.idle,
	pricingStatus: LoadingStatus.idle,
	usageStatus: LoadingStatus.idle,
	currentPage: [],
	totalPages: 1,
	totalResults: 0,
	searchParams: {
		searchTerm: '',
		pageIndex: 0,
		pageLimit: 40,
		corpSupplierCodesFilter: [],
		distributionCentersFilter: [],
		categoryLevel1Filter: undefined,
		categoryLevel2Filter: undefined,
		categoryLevel3Filter: undefined,
		categoryLevel4Filter: undefined,
		brandFilter: undefined,
		packSizeFilter: undefined,
		allowedStockTypes: User.isDssiSiteType()
			? [Hub.StockType.Stocked, Hub.StockType.Demand, Hub.StockType.Remote]
			: [
					Hub.StockType.Stocked,
					Hub.StockType.Demand,
					Hub.StockType.Remote,
					Hub.StockType.NonStock,
			  ],
	},
	filters: {
		supplierFacet: [],
		categoryLevel1Facet: [],
		categoryLevel2Facet: [],
		categoryLevel3Facet: [],
		categoryLevel4Facet: [],
		brandFacet: [],
		packSizeFacet: [],
	},
	blueprintCounts: blueprintCountsAdapter.getInitialState(),
	restrictionCounts: restrictionCountsAdapter.getInitialState(),
	suppliers: [],
	distributionCenterOptions: [],
	selectedDistributionCenters: [],
};

export const onSearchSubmit = createAsyncThunk<
	FetchSearchResults,
	FetchSearchResultsParams,
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'productSearchPage/onSearchSubmit',
	async (
		searchParams: FetchSearchResultsParams,
		{ dispatch, rejectWithValue }
	) => {
		dispatch(productSearchPageSlice.actions.searchParamsChange(searchParams));
		const result = await dispatch(fetchSearchResults(searchParams));

		if (fetchSearchResults.fulfilled.match(result)) {
			const searchResults = result.payload.productHeaders;
			const resultSet = result.payload;

			if (searchParams.packSizeFilter) {
				const validPackSizeFilters = searchParams.packSizeFilter.filter((x) =>
					resultSet.packSizeFacet?.find((y) => y.displayString == x)
				);
				if (validPackSizeFilters.length < searchParams.packSizeFilter.length) {
					dispatch(
						onSearchSubmit({
							...searchParams,
							packSizeFilter: validPackSizeFilters,
						})
					);
					return rejectWithValue(['Retrying with valid pack sizes']);
				}
			}

			if (!searchResults || searchResults.length <= 0) {
				return resultSet;
			}

			const priceParams = searchResults.map((product) => {
				return {
					corpSupplierCode: product.corpSupplierCode,
					supplierProductNumber: product.supplierProductNumber,
				};
			});
			const restrictionCountsParams = searchResults.map((product) => {
				return {
					corpSupplierDssiCode: product.corpSupplierCode,
					supplierProductNumber: product.supplierProductNumber,
					supplierCategory1Code: product.supplierCategoryLevel1Code,
					supplierCategory2Code: product.supplierCategoryLevel2Code,
					supplierCategory3Code: product.supplierCategoryLevel3Code,
					supplierCategory4Code: product.supplierCategoryLevel4Code,
					supplierCategory1Description:
						product.supplierCategoryLevel1Description,
					supplierCategory2Description:
						product.supplierCategoryLevel2Description,
					supplierCategory3Description:
						product.supplierCategoryLevel3Description,
					supplierCategory4Description:
						product.supplierCategoryLevel4Description,
				};
			});

			const supplierLookup = {};
			searchResults.forEach((product) => {
				if (!supplierLookup[product.corpSupplierCode]) {
					supplierLookup[product.corpSupplierCode] = [];
				}
				supplierLookup[product.corpSupplierCode].push(
					product.supplierProductNumber
				);
			});

			const blueprintCountParams: Hub.SupplierGroupArgs[] = [];
			Object.keys(supplierLookup).forEach((supplierCode) => {
				const productNumbers = supplierLookup[supplierCode];
				blueprintCountParams.push({
					corpSupplierDssiCode: supplierCode,
					supplierProductNumbers: productNumbers,
				});
			});

			Promise.all([
				dispatch(fetchHeaderLevelPrices(priceParams)),
				dispatch(fetchUsage(searchResults)),
				dispatch(fetchBlueprintCounts(blueprintCountParams)),
				dispatch(fetchRestrictionCounts(restrictionCountsParams)),
			]);

			return resultSet;
		} else if (
			fetchSearchResults.rejected.match(result) &&
			result.error?.name === 'AbortError'
		) {
			return rejectWithValue(['Aborted']);
		}
		return rejectWithValue(result.payload);
	}
);

export const fetchUsage = createAsyncThunk<
	void,
	IProductHeader[],
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'productSearchPage/fetchUsage',
	async (
		searchResults: IProductHeader[],
		{ getState, dispatch, rejectWithValue }
	) => {
		const state = getState();
		const customerId = LocalStorage.getCustomerId();
		const productGroups = lodash.groupBy(
			searchResults,
			(x) => x.corpSupplierCode
		);
		const productsGroupedBySupplier: ProductsGroupedBySupplier[] = [];

		const corpSupplierCodes = lodash.keys(productGroups);
		for (const corpSupplierCode of corpSupplierCodes) {
			const supplier = state.productSearchPage.suppliers.find(
				(supplier) => supplier.dssiCode == corpSupplierCode
			);
			let distributionCenterDssiCodes = [];

			if (supplier.distributionCenterCodes.length == 0) {
				const fetchDssiCodesArgs: fetchDssiCodesForCustomerAndSupplierArgs = {
					customerId,
					supplierId: supplier.id,
				};
				const dssiCodesResult = await dispatch(
					fetchDssiCodesForCustomerAndSupplier(fetchDssiCodesArgs)
				);
				if (
					fetchDssiCodesForCustomerAndSupplier.fulfilled.match(dssiCodesResult)
				) {
					distributionCenterDssiCodes = dssiCodesResult.payload;
				} else {
					rejectWithValue(dssiCodesResult.payload);
				}
			} else {
				distributionCenterDssiCodes = supplier.distributionCenterCodes;
			}

			productsGroupedBySupplier.push({
				corpSupplierCode,
				productNumbers: productGroups[corpSupplierCode].map(
					(x) => x.supplierProductNumber
				),
				distributionCenterDssiCodes: distributionCenterDssiCodes,
			});
		}
		dispatch(getProductUsageHistory({ productsGroupedBySupplier }));
	}
);

export const fetchDistributionCenters = createAsyncThunk<
	void,
	SupplierInfo,
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'productSearchPage/fetchDistributionCenters',
	async (supplier: SupplierInfo, { dispatch, rejectWithValue }) => {
		const customerId = LocalStorage.getCustomerId();
		let distributionCenterCodes: string[] = [];

		if (supplier.distributionCenterCodes.length === 0) {
			const fetchDssiCodesArgs: fetchDssiCodesForCustomerAndSupplierArgs = {
				customerId,
				supplierId: supplier.id,
			};
			const dssiCodesResult = await dispatch(
				fetchDssiCodesForCustomerAndSupplier(fetchDssiCodesArgs)
			);
			if (
				fetchDssiCodesForCustomerAndSupplier.fulfilled.match(dssiCodesResult)
			) {
				distributionCenterCodes = dssiCodesResult.payload;
			} else {
				rejectWithValue(dssiCodesResult.payload);
			}
		} else {
			distributionCenterCodes = supplier.distributionCenterCodes;
		}
		const distributionCenters: IDistributionCenter[] = [];
		const distributionCentersResult = await dispatch(
			fetchDistributionCentersForDssiCodes({
				distributionCenterDssiCodes: distributionCenterCodes,
			})
		);
		if (
			fetchDistributionCentersForDssiCodes.fulfilled.match(
				distributionCentersResult
			)
		) {
			distributionCenters.push(...distributionCentersResult.payload);
		} else {
			rejectWithValue(distributionCentersResult.payload);
		}

		const orderedDistributionCenters = lodash.sortBy(
			distributionCenters,
			(dc) => dc.name.toLowerCase()
		);

		dispatch(
			productSearchPageSlice.actions.setDistributionCenters({
				distributionCenters: orderedDistributionCenters,
				distributionCenterCodes,
			})
		);
	}
);

export const productSearchPageSlice = createSlice({
	name: 'productSearchPage',
	initialState,
	reducers: {
		searchTermChange: (state, action) => {
			state.searchParams.searchTerm = action.payload;
		},
		searchParamsChange: (state, action) => {
			state.searchParams = action.payload;
		},
		selectedProductKeyChange: (state, action) => {
			state.selectedProductKey = action.payload;
		},
		clearResults: (state) => {
			state.currentPage = initialState.currentPage;
			state.totalPages = initialState.totalPages;
			state.totalResults = initialState.totalResults;
		},
		setDistributionCenters: (state, action) => {
			const supplierIndex = state.suppliers.findIndex(
				(sup) => sup.dssiCode === state.searchParams.corpSupplierCodesFilter[0]
			);
			if (supplierIndex >= 0) {
				state.suppliers[supplierIndex].distributionCenterCodes =
					action.payload.distributionCenterCodes;
			}
			state.distributionCenterOptions = action.payload.distributionCenters;
		},
		dcFilterChange: (state, action) => {
			state.selectedDistributionCenters = action.payload;
		},
	},
	extraReducers: (builder) => {
		builder.addCase(onSearchSubmit.rejected, (state, action) => {
			const { payload } = action;
			if (payload instanceof Array && payload[0] === 'Aborted') {
				return;
			}
			state.searchStatus = LoadingStatus.failure;
			state.currentPage = initialState.currentPage;
			state.totalPages = initialState.totalPages;
			state.totalResults = initialState.totalResults;
		});
		builder.addCase(onSearchSubmit.pending, (state) => {
			state.searchStatus = LoadingStatus.loading;
		});
		builder.addCase(onSearchSubmit.fulfilled, (state, action) => {
			state.searchStatus = LoadingStatus.success;
			state.currentPage = action.payload.currentPage;
			state.totalPages = action.payload.totalPages;
			state.totalResults = action.payload.totalResults;
			state.filters = {
				supplierFacet: action.payload.supplierFacet,
				categoryLevel1Facet: action.payload.categoryLevel1Facet,
				categoryLevel2Facet: action.payload.categoryLevel2Facet,
				categoryLevel3Facet: action.payload.categoryLevel3Facet,
				categoryLevel4Facet: action.payload.categoryLevel4Facet,
				brandFacet: action.payload.brandFacet,
				packSizeFacet: sortPackSizeFacet(action.payload.packSizeFacet),
			};
		});
		builder.addCase(
			fetchCurrentCustomerSuppliers.fulfilled,
			(state, action) => {
				state.searchParams.corpSupplierCodesFilter = Object.keys(
					action.payload
				);
				state.suppliers = Object.values(action.payload).map((supplier) => {
					return {
						...supplier,
						distributionCenterCodes: [],
					};
				});
			}
		);
		builder.addCase(fetchHeaderLevelPrices.rejected, (state) => {
			state.pricingStatus = LoadingStatus.failure;
		});
		builder.addCase(fetchHeaderLevelPrices.pending, (state) => {
			state.pricingStatus = LoadingStatus.loading;
		});
		builder.addCase(fetchHeaderLevelPrices.fulfilled, (state) => {
			state.pricingStatus = LoadingStatus.success;
		});

		builder.addCase(fetchBlueprintCounts.fulfilled, (state, action) => {
			blueprintCountsAdapter.setAll(state.blueprintCounts, action.payload);
		});
		builder.addCase(fetchRestrictionCounts.fulfilled, (state, action) => {
			restrictionCountsAdapter.setAll(state.restrictionCounts, action.payload);
		});
		builder.addCase(fetchDistributionCenters.pending, (state) => {
			state.selectedDistributionCenters =
				initialState.selectedDistributionCenters;
		});
		builder.addCase(getProductUsageHistory.pending, (state) => {
			state.usageStatus = LoadingStatus.loading;
		});
		builder.addCase(getProductUsageHistory.rejected, (state) => {
			state.usageStatus = LoadingStatus.failure;
		});
		builder.addCase(getProductUsageHistory.fulfilled, (state) => {
			state.usageStatus = LoadingStatus.success;
		});
	},
});

export const onPageWillLoad = createAsyncThunk<
	void,
	{},
	{ state: IAppState; rejectValue: string[] }
>(
	'productSearchPage/onPageWillLoad',
	async (params: {}, { dispatch, getState }) => {
		const state = getState();
		dispatch(
			productSearchPageSlice.actions.searchParamsChange({
				...initialState.searchParams,
				corpSupplierCodesFilter:
					state.productSearchPage.searchParams.corpSupplierCodesFilter,
			})
		);
		dispatch(productSearchPageSlice.actions.dcFilterChange([]));
		dispatch(productSearchPageSlice.actions.clearResults());
	}
);

export const onPageMount = createAsyncThunk<
	void,
	{},
	{ state: IAppState; rejectValue: string[] }
>('productSearchPage/onPageMount', async (params: {}, { dispatch }) => {
	const result = await dispatch(fetchCurrentCustomerSuppliers());
	if (fetchCurrentCustomerSuppliers.fulfilled.match(result)) {
		if (Object.keys(result.payload).length === 1) {
			const supplier: SupplierInfo = {
				...Object.values(result.payload)[0],
				distributionCenterCodes: [],
			};
			await dispatch(fetchDistributionCenters(supplier));
		}
	}
});

export enum SearchFilterType {
	Supplier,
	CategoryLevel1,
	CategoryLevel2,
	CategoryLevel3,
	CategoryLevel4,
	Brand,
	PackSize,
}
export interface SearchFilterChangedArgs {
	type: SearchFilterType;
	values: string[] | null;
}
export const onSearchFilterChange = createAsyncThunk<
	void,
	SearchFilterChangedArgs,
	{ state: IAppState; rejectValue: string[] }
>(
	'productSearchPage/onSearchFilterChange',
	async (
		filterChangedArgs: SearchFilterChangedArgs,
		{ dispatch, getState }
	) => {
		const state = getState();
		const searchParams = {
			...state.productSearchPage.searchParams,
			pageIndex: 0,
		};
		switch (filterChangedArgs.type) {
			case SearchFilterType.Supplier: {
				const oldSupplierFilter = searchParams.corpSupplierCodesFilter;
				searchParams.corpSupplierCodesFilter =
					filterChangedArgs.values && filterChangedArgs.values[0]
						? filterChangedArgs.values
						: Object.keys(state.suppliers.customerSuppliersByCode);
				searchParams.categoryLevel1Filter = undefined;
				searchParams.categoryLevel2Filter = undefined;
				searchParams.categoryLevel3Filter = undefined;
				searchParams.categoryLevel4Filter = undefined;
				search;
				if (filterChangedArgs.values && filterChangedArgs.values[0]) {
					const supplier = state.productSearchPage.suppliers.find(
						(supplier) => supplier.dssiCode == filterChangedArgs.values[0]
					);
					dispatch(fetchDistributionCenters(supplier));
					if (
						oldSupplierFilter.length != 1 ||
						oldSupplierFilter[0] != filterChangedArgs.values[0]
					) {
						searchParams.distributionCentersFilter = [];
					}
				} else {
					searchParams.distributionCentersFilter = [];
				}
				dispatch(productSearchPageSlice.actions.dcFilterChange([]));
				break;
			}
			case SearchFilterType.CategoryLevel1:
				searchParams.categoryLevel1Filter = filterChangedArgs.values?.[0];
				searchParams.categoryLevel2Filter = undefined;
				searchParams.categoryLevel3Filter = undefined;
				searchParams.categoryLevel4Filter = undefined;
				break;
			case SearchFilterType.CategoryLevel2:
				searchParams.categoryLevel2Filter = filterChangedArgs.values?.[0];
				searchParams.categoryLevel3Filter = undefined;
				searchParams.categoryLevel4Filter = undefined;
				break;
			case SearchFilterType.CategoryLevel3:
				searchParams.categoryLevel3Filter = filterChangedArgs.values?.[0];
				searchParams.categoryLevel4Filter = undefined;
				break;
			case SearchFilterType.CategoryLevel4:
				searchParams.categoryLevel4Filter = filterChangedArgs.values?.[0];
				break;
			case SearchFilterType.Brand:
				searchParams.brandFilter = filterChangedArgs.values?.[0];
				break;
			case SearchFilterType.PackSize:
				searchParams.packSizeFilter = filterChangedArgs.values;
				break;
		}
		dispatch(onSearchSubmit(searchParams));
	}
);

export const onPageChange = createAsyncThunk<
	void,
	number,
	{ state: IAppState; rejectValue: string[] }
>(
	'productSearchPage/onPageChange',
	async (pageIndex: number, { dispatch, getState }) => {
		const state = getState();
		const searchParams = {
			...state.productSearchPage.searchParams,
			pageIndex,
		};
		dispatch(onSearchSubmit(searchParams));
	}
);

export const changeSearchDcFilter = createAsyncThunk<
	void,
	IDistributionCenter[],
	{ state: IAppState }
>(
	'productSearchPage/changeSearchDcFilter',
	async (dcFilter: IDistributionCenter[], { dispatch, getState }) => {
		const state = getState();
		const searchParams = {
			...state.productSearchPage.searchParams,
			pageIndex: 0,
		};

		let dcs = [];
		if (dcFilter.length > 0) {
			dcs = dcFilter.map((dc) => dc.dssiCode);
		}

		dispatch(productSearchPageSlice.actions.dcFilterChange(dcFilter));

		searchParams.distributionCentersFilter = dcs;

		await dispatch(onSearchSubmit(searchParams));
	}
);

export const requestLocationLevelPricingReport = createAsyncThunk<
	void,
	{ corpSupplierCode: string; productNumber: string; um: string },
	{ state: IAppState }
>(
	'productSearchPage/requestLocationLevelPricingReport',
	async (
		args: { corpSupplierCode: string; productNumber: string; um: string },
		{ getState }
	) => {
		try {
			const state = getState();
			const supplier =
				state.suppliers.customerSuppliersByCode[args.corpSupplierCode];
			const params: Hub.RequestLocationLevelPricingReportArgs = {
				supplierId: supplier?.id,
				supplierProductNumber: args.productNumber,
				unitOfMeasure: args.um,
			};
			const result = await unwrapFetch2(
				Hub.ReportController.RequestLocationLevelPricingReport(params)
			);
			if (result.errors.length == 0) {
				reportSuccessfullyRequested();
			} else {
				unknownErrorRequestingReport();
			}
		} catch (err) {
			if ((err as Error).message.includes('report-in-progress')) {
				reportCurrentlyInProgress();
			} else {
				unknownErrorRequestingReport();
			}
		}
	}
);

const unknownErrorRequestingReport = () => {
	toastr.error({
		title: 'Report Request Failed',
		body: 'The report was not successfully requested.',
	});
};
const reportCurrentlyInProgress = () => {
	toastr.warning({
		title: 'Report Request Failed',
		body: 'Another report request is currently in progress',
	});
};
const reportSuccessfullyRequested = () => {
	toastr.success({
		title: 'Report Request Sent',
		body: 'The report was successfully requested.',
	});
};
